import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { getSync } from '../../api';
import { invalidateConversationLists } from '../conversation';
import { syncKey } from './keys';

/** De quanto em quanto tempo o andamento é consultado enquanto a importação roda. */
export const SYNC_POLL_MS = 2000;

/**
 * Andamento da importação de histórico. Enquanto o estado é `running` consulta a cada 2 s; ao terminar
 * (`done`/`error`) a consulta para sozinha, e também para ao sair da tela (a consulta perde o
 * observador) e com a aba oculta (`refetchIntervalInBackground: false`; ao voltar, o foco refaz a
 * consulta). Dado `null` = o servidor não tem importação.
 *
 * Quando uma importação TERMINA (o `finishedAt` muda em relação ao que a tela já tinha visto), as
 * listas de conversas ficam obsoletas: a importação trouxe conversas e mensagens novas.
 */
export function useSyncStatus() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: syncKey,
    queryFn: ({ signal }) => getSync(signal),
    staleTime: 0,
    refetchInterval: (current) => (current.state.data?.state === 'running' ? SYNC_POLL_MS : false),
    refetchIntervalInBackground: false,
  });

  const finishedAt = query.data?.finishedAt ?? null;
  // undefined = a tela ainda não viu nenhum andamento (o primeiro nunca dispara: não é uma mudança).
  const seen = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!query.data) return;
    const previous = seen.current;
    seen.current = finishedAt;
    if (previous !== undefined && finishedAt !== null && finishedAt !== previous) {
      void invalidateConversationLists(queryClient);
    }
  }, [query.data, finishedAt, queryClient]);

  return query;
}
